import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RefreshCw, User, Database, AlertTriangle } from 'lucide-react';

interface DebugInfo {
  userId: string | null;
  userEmail: string | null;
  profileExists: boolean;
  username: string | null;
  lastError: string | null;
  profileData: any;
}

export function AuthDebug() {
  const { user, session } = useAuth();
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({
    userId: null,
    userEmail: null,
    profileExists: false,
    username: null,
    lastError: null,
    profileData: null,
  });
  const [loading, setLoading] = useState(false);

  const refreshDebugInfo = async () => {
    setLoading(true);
    try {
      const info: DebugInfo = {
        userId: user?.id || null,
        userEmail: user?.email || null,
        profileExists: false,
        username: null,
        lastError: null,
        profileData: null,
      };

      if (user) {
        // Check if profile exists in users table
        const { data: profile, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          info.lastError = `${error.code}: ${error.message}`;
        } else if (profile) {
          info.profileExists = true;
          info.username = profile.username;
          info.profileData = profile;
        }
      }

      setDebugInfo(info);
    } catch (error: any) {
      setDebugInfo(prev => ({
        ...prev,
        lastError: error.message || 'Unexpected error'
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshDebugInfo();
  }, [user]);

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 right-4 w-80 max-h-96 overflow-auto z-50 shadow-lg border-2 border-yellow-500">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-yellow-500" />
          Auth Debug Panel
          <Button 
            size="sm" 
            variant="outline" 
            onClick={refreshDebugInfo}
            disabled={loading}
            className="ml-auto"
          >
            {loading ? (
              <RefreshCw className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <User className="w-3 h-3" />
            <span className="font-semibold">User Status</span>
          </div>
          <div className="pl-5 space-y-1">
            <div>ID: {debugInfo.userId || 'None'}</div>
            <div>Email: {debugInfo.userEmail || 'None'}</div>
            <div>Session: {session ? 'Active' : 'None'}</div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Database className="w-3 h-3" />
            <span className="font-semibold">Profile Status</span>
          </div>
          <div className="pl-5 space-y-1">
            <div className="flex items-center gap-2">
              Exists: 
              <Badge variant={debugInfo.profileExists ? 'default' : 'destructive'}>
                {debugInfo.profileExists ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              Username: 
              <Badge variant={debugInfo.username ? 'default' : 'secondary'}>
                {debugInfo.username || 'Not Set'}
              </Badge>
            </div>
          </div>
        </div>

        {debugInfo.lastError && (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">
              <strong>Last Error:</strong> {debugInfo.lastError}
            </AlertDescription>
          </Alert>
        )}

        {debugInfo.profileData && (
          <details className="text-xs">
            <summary className="cursor-pointer font-semibold">Profile Data</summary>
            <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-auto">
              {JSON.stringify(debugInfo.profileData, null, 2)}
            </pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}